import { SHOW_REACTIONS } from "@app/constants";
import { fetchReactionList } from "@app/layouts/Article/ReactionWidget/reactionsApi";
import ReactionSummary from "@app/layouts/Article/ReactionWidget/ReactionSummary";
import { CardReactionsProvider as DSCardReactionsProvider } from "@sphtech/dmg-design-system";
import type { ReactionsListResponse } from "@src/app/types/Reactions";
import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";

const BATCH_DEBOUNCE_MS = 50;
const MAX_IDS_PER_REQUEST = 20;

/**
 * Provides reaction summaries to design-system cards. Cards self-register their
 * article id; ids are debounced + batched into `get-reaction-list` calls, and
 * the resulting counts are rendered as a compact <ReactionSummary> per card.
 * No-op for pubs where reactions are disabled.
 */
export function CardReactionsProvider({ children }: { children: ReactNode }) {
  const [map, setMap] = useState<ReactionsListResponse>({});
  const pendingRef = useRef<Set<string>>(new Set());
  const requestedRef = useRef<Set<string>>(new Set());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    const ids = [...pendingRef.current];
    pendingRef.current.clear();
    if (ids.length === 0) return;
    ids.forEach((id) => requestedRef.current.add(id));

    for (let i = 0; i < ids.length; i += MAX_IDS_PER_REQUEST) {
      const chunk = ids.slice(i, i + MAX_IDS_PER_REQUEST);
      void fetchReactionList(chunk)
        .then((res) => {
          setMap((prev) => ({ ...prev, ...res }));
        })
        .catch(() => {
          /* ignore — cards just won't show reactions */
        });
    }
  }, []);

  const register = useCallback(
    (id: string) => {
      // Only numeric CUE ids are valid reaction keys; skip anything else and
      // anything already requested.
      if (
        !/^\d+$/.test(id) ||
        requestedRef.current.has(id) ||
        pendingRef.current.has(id)
      ) {
        return;
      }
      pendingRef.current.add(id);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, BATCH_DEBOUNCE_MS);
    },
    [flush],
  );

  const render = useCallback(
    (id: string): ReactNode => {
      const reactions = map[id]?.reactions;
      return reactions && reactions.length > 0 ? (
        <ReactionSummary reactions={reactions} variant="card" />
      ) : null;
    },
    [map],
  );

  const value = useMemo(() => ({ register, render }), [register, render]);

  if (!SHOW_REACTIONS) {
    return <>{children}</>;
  }

  return (
    <DSCardReactionsProvider value={value}>{children}</DSCardReactionsProvider>
  );
}
